import type {
  ConnectOptions,
  MediapipeHandResults,
  MediapipeLandmark,
} from "/@/shared/motion-menu-system/MenuTypes";
import type { IIOStream } from "./types.ts";

// MediaPipe hand landmark indices
const WRIST = 0;
const THUMB_TIP = 4;
const INDEX_TIP = 8;
const MIDDLE_TIP = 12;
const MIDDLE_MCP = 9; // Middle finger metacarpal (base)

interface ScalarResults {
  hands?: MediapipeHandResults;
  pose?: any;
  scalarControlType?: string;
}

interface RenderScalarControlParams {
  canvasCtx: CanvasRenderingContext2D;
  scalarResults: ScalarResults;
  canvasAlreadyMirrored?: boolean; // Whether the canvas context is already horizontally mirrored
  options: ConnectOptions;
}

// scalarControlType: [single-hand-thumb-fingers, two-hand-palms]
export const renderScalarControl = ({
  canvasCtx,
  scalarResults,
  // canvasAlreadyMirrored = false,
  options,
}: RenderScalarControlParams): void => {
  const { hands, pose, scalarControlType } = scalarResults;

  if (!hands?.landmarks || !hands.landmarks.length) return;

  if (
    !(options.scalarRendering1Hand || options.scalarRendering1HandLeft ||
      options.scalarRendering1HandRight || options.scalarRendering2Hands)
  ) {
    return;
  }

  switch (scalarControlType) {
    case "single-hand-thumb-fingers": {
      if (!options.scalarRendering1Hand) {
        break;
      }
      // Draw line between thumb tip and index finger tip
      if (hands.landmarks[0]) {
        canvasCtx.strokeStyle = "#87CEEB"; // Light blue
        canvasCtx.lineWidth = 5;
        const thumb = hands.landmarks[0][THUMB_TIP];
        const index = hands.landmarks[0][MIDDLE_TIP];
        // Calculate X coordinates
        const thumbX = thumb.x * canvasCtx.canvas.width;
        const thumbY = thumb.y * canvasCtx.canvas.height;
        const indexX = index.x * canvasCtx.canvas.width;
        const indexY = index.y * canvasCtx.canvas.height;

        canvasCtx.beginPath();
        canvasCtx.moveTo(thumbX, thumbY);
        canvasCtx.lineTo(indexX, indexY);
        canvasCtx.stroke();
      }
      break;
    }
    case "two-hand-palms": {
      if (!options.scalarRendering2Hands) {
        break;
      }
      // Draw line between middle fingers of both hands
      if (
        hands.landmarks.length >= 2 &&
        hands.landmarks[0] &&
        hands.landmarks[1]
      ) {
        canvasCtx.strokeStyle = "#87CEEB"; // Light blue
        canvasCtx.lineWidth = 5;

        const leftMiddle = hands.landmarks[0][MIDDLE_TIP];
        const rightMiddle = hands.landmarks[1][MIDDLE_TIP];

        // Calculate X coordinates
        const leftMiddleX = leftMiddle.x * canvasCtx.canvas.width;
        const leftMiddleY = leftMiddle.y * canvasCtx.canvas.height;
        const rightMiddleX = rightMiddle.x * canvasCtx.canvas.width;
        const rightMiddleY = rightMiddle.y * canvasCtx.canvas.height;

        canvasCtx.beginPath();
        canvasCtx.moveTo(leftMiddleX, leftMiddleY);
        canvasCtx.lineTo(rightMiddleX, rightMiddleY);
        canvasCtx.stroke();
      }
      break;
    }
    default:
      throw new Error(`Unknown scalar control type: ${scalarControlType}`);
  }
};

interface ClenchData {
  isClenchLeft?: boolean;
  isClenchRight?: boolean;
}

interface ScalarInputs {
  hands?: MediapipeHandResults;
  pose?: any;
  clench?: ClenchData;
}

interface ScalarOutput {
  hands: MediapipeHandResults;
  pose?: any;
  scalarControlType: string;
  value: number;
}

interface GenerateScalarControllerParams {
  iostream: IIOStream;
}

export interface ScalarController {
  dispose: () => void;
  onInputs: (inputs: ScalarInputs) => ScalarOutput | undefined;
  root: HTMLDivElement;
}

export const generateScalarController = ({
  iostream,
}: GenerateScalarControllerParams): ScalarController => {
  if (iostream === undefined) {
    throw new Error("iostream is required");
  }
  let cancelled = false;
  const root = document.createElement("div");

  // Position in top right corner
  root.style.position = "absolute";
  root.style.top = "1rem";
  root.style.right = "1rem";
  root.style.width = "200px";
  root.style.height = "15px";
  root.style.zIndex = "1000";

  // Initially hide the controller
  root.style.opacity = "0";
  root.style.visibility = "hidden";
  root.style.transition = "opacity 0.3s, visibility 0.3s";

  // Create slider bar
  const barTrack = document.createElement("div");
  barTrack.style.position = "relative";
  barTrack.style.width = "100%";
  barTrack.style.height = "100%";
  barTrack.style.backgroundColor = "rgba(0, 0, 0, 0.3)";
  barTrack.style.borderRadius = "15px";
  barTrack.style.overflow = "hidden";

  const barFill = document.createElement("div");
  barFill.style.position = "absolute";
  barFill.style.top = "0";
  barFill.style.left = "0";
  barFill.style.height = "100%";
  barFill.style.width = "0%";
  barFill.style.backgroundColor = "#4285f4";
  barFill.style.transition = "width 0.1s ease";

  const barLabel = document.createElement("div");
  barLabel.style.position = "absolute";
  barLabel.style.top = "50%";
  barLabel.style.left = "50%";
  barLabel.style.transform = "translate(-50%, -50%)";
  barLabel.style.color = "white";
  barLabel.style.fontSize = "12px";
  barLabel.style.fontWeight = "bold";
  barLabel.style.textShadow = "0 0 4px rgba(0,0,0,0.5)";
  barLabel.style.zIndex = "2";
  barLabel.textContent = "";

  barTrack.appendChild(barFill);
  barTrack.appendChild(barLabel);
  root.appendChild(barTrack);

  // Calculate distance between two landmarks (2D, using x,y)
  function calculateDistance(
    lm1: MediapipeLandmark,
    lm2: MediapipeLandmark,
  ): number {
    const dx = lm1.x - lm2.x;
    const dy = lm1.y - lm2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Store min/max values for normalization
  // let minDistance = 0;
  // let maxDistance = 0.1; // Typical max distance between thumb and index finger
  const rangeSingleHand = [0.019, 0.14];
  const rangeTwoHands = [0.02, 0.7];

  // Handle inputs {hands, pose}
  const onInputs = (inputs: ScalarInputs): ScalarOutput | undefined => {
    if (cancelled) return;

    const { hands, pose, clench } = inputs;
    if (
      !hands?.landmarks || !hands.landmarks.length || !hands?.worldLandmarks ||
      !hands.worldLandmarks.length
    ) {
      root.style.opacity = "0";
      root.style.visibility = "hidden";
      return undefined;
    }

    // Check if any hand is clenched
    const isLeftClenched = clench?.isClenchLeft;
    const isRightClenched = clench?.isClenchRight;

    let visibleHands: { index: number; landmarks: MediapipeLandmark[] }[] = [];
    let visibleWorldHands: { index: number; landmarks: MediapipeLandmark[] }[] =
      [];
    for (let i = 0; i < hands.landmarks.length; i++) {
      const isClenched = (i === 0 && isLeftClenched) ||
        (i === 1 && isRightClenched);
      if (!isClenched) {
        visibleHands.push({ index: i, landmarks: hands.landmarks[i] });
        visibleWorldHands.push({
          index: i,
          landmarks: hands.worldLandmarks[i],
        });
      }
    }

    if (visibleHands.length === 0) {
      root.style.opacity = "0";
      root.style.visibility = "hidden";
      return undefined;
    }

    let distance = 0;
    let scalarControlType: string | null = null;

    if (visibleHands.length === 1) {
      // Single hand: use distance between thumb and index finger
      const hand = visibleWorldHands[0];
      const thumbTip = hand.landmarks[THUMB_TIP];
      const indexTip = hand.landmarks[MIDDLE_TIP];
      distance = calculateDistance(thumbTip, indexTip);

      scalarControlType = "single-hand-thumb-fingers";

      // Update max distance for normalization
      // maxDistance = Math.max(maxDistance, distance * 1.5); // Allow some headroom
      // maxDistance = Math.max(maxDistance, distance * 0.9); // Allow some headroom

      // Normalize distance to 0-1 range
      // const normalizedValue = Math.min(1, distance / maxDistance);

      let normalizedValue = (distance - rangeSingleHand[0]) /
        (rangeSingleHand[1] - rangeSingleHand[0]);
      normalizedValue = parseFloat(
        Math.max(0, Math.min(1, normalizedValue)).toFixed(2),
      );

      // Update bar fill and label
      if (globalThis.options.scalarRenderingBar) {
        barFill.style.width = `${normalizedValue * 100}%`;
        barLabel.textContent = normalizedValue.toFixed(2);
        root.style.opacity = "1";
        root.style.visibility = "visible";
      } else {
        root.style.opacity = "0";
        root.style.visibility = "hidden";
      }

      iostream.setOutputs({
        UnitScalar: normalizedValue,
        "UnitScalar.1hand": normalizedValue,
      });

      return {
        hands: { landmarks: visibleHands.map((h) => h.landmarks) },
        pose,
        scalarControlType,
        value: normalizedValue,
      };
    } else if (visibleHands.length >= 2) {
      // Two hands: use distance between middle finger tips
      const hand1 = visibleHands[0];
      const hand2 = visibleHands[1];
      const middleTip1 = hand1.landmarks[MIDDLE_TIP];
      const middleTip2 = hand2.landmarks[MIDDLE_TIP];
      distance = calculateDistance(middleTip1, middleTip2);
      scalarControlType = "two-hand-palms";

      // Update max distance for normalization
      // maxDistance = Math.max(maxDistance, distance * 1.5); // Allow some headroom

      // Normalize distance to 0-1 range
      // let normalizedValue = Math.min(1, distance / maxDistance);
      let normalizedValue = (distance - rangeTwoHands[0]) /
        (rangeTwoHands[1] - rangeTwoHands[0]);
      normalizedValue = parseFloat(
        Math.max(0, Math.min(1, normalizedValue)).toFixed(2),
      );

      // Update bar fill and label
      if (globalThis.options.scalarRenderingBar) {
        barFill.style.width = `${normalizedValue * 100}%`;
        barLabel.textContent = normalizedValue.toFixed(2);
        root.style.opacity = "1";
        root.style.visibility = "visible";
      } else {
        root.style.opacity = "0";
        root.style.visibility = "hidden";
      }

      iostream.setOutputs({
        UnitScalar: normalizedValue,
        "UnitScalar.2hands": normalizedValue,
      });

      return {
        hands: { landmarks: visibleHands.map((h) => h.landmarks) },
        pose,
        scalarControlType,
        value: normalizedValue,
      };
    }

    return undefined;
  };

  const dispose = (): void => {
    cancelled = true;
    root.remove();
  };

  return {
    dispose,
    onInputs,
    root,
  };
};
